import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { Tooltip, FormFieldError } from '@kira/checkpoint-shared-frontend';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import Banner, { BannerVariant } from 'components/Banner/Banner';
import CTAModal from 'components/CTAModal/CTAModal';
import Checkbox from 'components/Checkbox/Checkbox';
import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { AssessmentEmailSettings } from 'hooks/useGetAssessmentEmailSettings';
import useNotification from 'hooks/useNotification';
import useUpdateAssessmentEmailSettings from 'hooks/useUpdateAssessmentEmailSettings';
import { t } from 'locales/i18n';

const APPLICANT_CREATED_AT = 'applicant_created_at';
const APPLICANT_DEADLINE = 'applicant_deadline';
const INTERVIEW_CLOSED_DATE = 'interview_closed_date';

enum FormFields {
  ENABLE = 'enable',
  APPLICANT_CREATED_AT_5_DAYS = `${APPLICANT_CREATED_AT}_5days`,
  APPLICANT_CREATED_AT_2_WEEKS = `${APPLICANT_CREATED_AT}_2weeks`,
  APPLICANT_DEADLINE_2_DAYS = `${APPLICANT_DEADLINE}_2days`,
  APPLICANT_DEADLINE_1_WEEK = `${APPLICANT_DEADLINE}_1week`,
  INTERVIEW_CLOSED_DATE_2_DAYS = `${INTERVIEW_CLOSED_DATE}_2days`,
  INTERVIEW_CLOSED_DATE_1_WEEK = `${INTERVIEW_CLOSED_DATE}_1week`,
}

interface FormData {
  [FormFields.ENABLE]: boolean;
  [FormFields.APPLICANT_CREATED_AT_5_DAYS]: boolean;
  [FormFields.APPLICANT_CREATED_AT_2_WEEKS]: boolean;
  [FormFields.APPLICANT_DEADLINE_2_DAYS]: boolean;
  [FormFields.APPLICANT_DEADLINE_1_WEEK]: boolean;
  [FormFields.INTERVIEW_CLOSED_DATE_2_DAYS]: boolean;
  [FormFields.INTERVIEW_CLOSED_DATE_1_WEEK]: boolean;
}
interface RemindersProps {
  interviewUid: string;
  data?: AssessmentEmailSettings;
}

const Reminders = ({ interviewUid, data }: RemindersProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const queryClient = useQueryClient();
  const disableNotifications = !data?.allow_applicant_notifications;

  const sections = [
    {
      title: t('After the applicant is invited'),
      description: t(
        'Reminders will be sent based on when applicants were invited to the assessment.'
      ),
      disabled: false,
      disableMessage: '',
      reminders: [
        {
          label: t('5 days after applicant is invited'),
          name: FormFields.APPLICANT_CREATED_AT_5_DAYS,
        },
        {
          label: t('2 weeks after applicant is invited'),
          name: FormFields.APPLICANT_CREATED_AT_2_WEEKS,
          disabled: false,
          disableMessage: '',
        },
      ],
    },
    {
      title: t('Before the applicant’s deadline date'),
      description: t(
        'Reminders will be sent based on rolling applicant deadlines. Rolling deadlines can be managed in the General tab.'
      ),
      disabled: !data?.has_rolling_applicant_deadline ?? true,
      disableMessage: t(
        'These fields are only available when rolling deadlines are enabled. Go to the General tab in Settings to set this up.'
      ),
      reminders: [
        {
          label: t('1 week before applicant’s deadline'),
          name: FormFields.APPLICANT_DEADLINE_1_WEEK,
        },
        {
          label: t('2 days before applicant’s deadline'),
          name: FormFields.APPLICANT_DEADLINE_2_DAYS,
        },
      ],
    },
    {
      title: t('Before the assessment’s close date'),
      description: t(
        'Reminders will be sent based on the assessments close date. Interview close dates can be managed in the General tab.'
      ),
      disabled: data?.closed_date === null,
      disableMessage: t(
        'These triggers are only available when the interview close date is set. Go to the General tab in Settings to set this up.'
      ),
      reminders: [
        {
          label: t('1 week before interview close date'),
          name: FormFields.INTERVIEW_CLOSED_DATE_1_WEEK,
        },
        {
          label: t('2 days before interview close date'),
          name: FormFields.INTERVIEW_CLOSED_DATE_2_DAYS,
        },
      ],
    },
  ];

  const { updateAssessmentEmailSettings, isLoading: isLoadingUpdateSettings } =
    useUpdateAssessmentEmailSettings();

  const defaultFormValues = useMemo(() => {
    if (!data) {
      return {
        [FormFields.ENABLE]: false,
        [FormFields.APPLICANT_CREATED_AT_5_DAYS]: false,
        [FormFields.APPLICANT_CREATED_AT_2_WEEKS]: false,
        [FormFields.APPLICANT_DEADLINE_2_DAYS]: false,
        [FormFields.APPLICANT_DEADLINE_1_WEEK]: false,
        [FormFields.INTERVIEW_CLOSED_DATE_2_DAYS]: false,
        [FormFields.INTERVIEW_CLOSED_DATE_1_WEEK]: false,
      };
    }
    return {
      [FormFields.ENABLE]: data.has_auto_reminders,
      [FormFields.APPLICANT_CREATED_AT_5_DAYS]: data.reminders.some(
        (item) => item.rule === APPLICANT_CREATED_AT && item.days === 5
      ),
      [FormFields.APPLICANT_CREATED_AT_2_WEEKS]: data.reminders.some(
        (item) => item.rule === APPLICANT_CREATED_AT && item.days === 14
      ),
      [FormFields.APPLICANT_DEADLINE_2_DAYS]: data.reminders.some(
        (item) => item.rule === APPLICANT_DEADLINE && item.days === 2
      ),
      [FormFields.APPLICANT_DEADLINE_1_WEEK]: data.reminders.some(
        (item) => item.rule === APPLICANT_DEADLINE && item.days === 7
      ),
      [FormFields.INTERVIEW_CLOSED_DATE_2_DAYS]: data.reminders.some(
        (item) => item.rule === INTERVIEW_CLOSED_DATE && item.days === 2
      ),
      [FormFields.INTERVIEW_CLOSED_DATE_1_WEEK]: data.reminders.some(
        (item) => item.rule === INTERVIEW_CLOSED_DATE && item.days === 7
      ),
    };
  }, [data]);

  const onSubmit: SubmitHandler<FormData> = async (values) => {
    const reminders = [];
    if (values[FormFields.APPLICANT_CREATED_AT_5_DAYS]) {
      reminders.push({ rule: APPLICANT_CREATED_AT, days: 5 });
    }
    if (values[FormFields.APPLICANT_CREATED_AT_2_WEEKS]) {
      reminders.push({ rule: APPLICANT_CREATED_AT, days: 14 });
    }
    if (values[FormFields.APPLICANT_DEADLINE_1_WEEK]) {
      reminders.push({ rule: APPLICANT_DEADLINE, days: 7 });
    }
    if (values[FormFields.APPLICANT_DEADLINE_2_DAYS]) {
      reminders.push({ rule: APPLICANT_DEADLINE, days: 2 });
    }
    if (values[FormFields.INTERVIEW_CLOSED_DATE_1_WEEK]) {
      reminders.push({ rule: INTERVIEW_CLOSED_DATE, days: 7 });
    }
    if (values[FormFields.INTERVIEW_CLOSED_DATE_2_DAYS]) {
      reminders.push({ rule: INTERVIEW_CLOSED_DATE, days: 2 });
    }

    await updateAssessmentEmailSettings(
      {
        interviewUid,
        isEnable: values.enable,
        reminders,
      },
      {
        onSuccess: () => {
          setErrorMessage(undefined);
          showSuccessNotification(
            t('Assessment settings saved'),
            t('Your settings were successfully saved')
          );
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentEmailSettings', interviewUid],
          });
        },
        onError: (error) => {
          if (
            error.cause &&
            (error.cause as { status: number }).status === 400
          ) {
            setErrorMessage(error.message);
          } else {
            showErrorNotification(
              t('An error occurred while updating assessment'),
              error.message
            );
          }
          return false;
        },
      }
    );
  };

  const handleConfirmation = (e: React.FormEvent) => {
    setIsModalOpen(true);
    e.preventDefault();
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    reset(defaultFormValues);
  }, [data]);

  const reminderFieldsClassName = twMerge(
    'flex',
    'w-ful',
    'transition-all',
    'mt-6',
    watch(FormFields.ENABLE) ? 'h-auto' : 'h-0',
    watch(FormFields.ENABLE) ? '' : 'overflow-hidden'
  );

  const informationCircleClassName = twMerge(
    'ml-1',
    'inline-block',
    'h-4',
    'w-4',
    'text-kira-slate-700'
  );

  const tooltipContainerClassName = twMerge(
    'z-50 text-left whitespace-pre-line'
  );

  return (
    <form
      onSubmit={
        watch(FormFields.ENABLE) === false
          ? handleConfirmation
          : handleSubmit(onSubmit)
      }
    >
      {disableNotifications && (
        <Banner
          message={t(
            'Applicant notifications are disabled for this assessment.'
          )}
          contactLink
          requestChangesTo="CAS ID"
          variant={BannerVariant.INFO}
        />
      )}
      <InlineForm
        title={
          <div className="flex items-center">
            {t('Automated reminder emails')}
            <Tooltip
              tooltipClassNames={tooltipContainerClassName}
              textContent={t(
                'Multiple reminder email options can be selected. While only incomplete applicants will receive an automatic reminder, emails can still be sent manually.'
              )}
            >
              <InformationCircleIcon className={informationCircleClassName} />
            </Tooltip>
          </div>
        }
        description={t(
          'Send automated reminder emails to applicants based on different events.'
        )}
        childrenClassNames="lg:pl-12"
      >
        <div className="lg:pl-12">
          <Controller
            name={FormFields.ENABLE}
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                disabled={disableNotifications}
                enabled={value}
                setEnabled={onChange}
              />
            )}
          />
        </div>
      </InlineForm>
      <div className={reminderFieldsClassName}>
        <div className="w-full space-y-6 border-l-4 border-kira-slate-200 pl-4">
          {sections.map((section) => (
            <InlineForm
              key={section.title}
              title={section.title}
              description={section.description}
              childrenClassNames="lg:pl-8"
            >
              <div className="grid gap-4 lg:pl-12">
                {section.reminders.map((reminder) => (
                  <Controller
                    key={reminder.name}
                    name={reminder.name}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Tooltip
                        tooltipClassNames={tooltipContainerClassName}
                        textContent={
                          section.disabled ? section.disableMessage : ''
                        }
                      >
                        <Checkbox
                          disabled={section.disabled}
                          checked={value}
                          id={reminder.label}
                          name={reminder.label}
                          onChange={onChange}
                        />
                      </Tooltip>
                    )}
                  />
                ))}
              </div>
            </InlineForm>
          ))}
          <InlineForm title="" childrenClassNames="lg:pl-8 -mt-4">
            <div className="grid gap-4 lg:pl-12">
              <FormFieldError
                isVisible={!!errorMessage}
                message={errorMessage ?? ''}
                className="mt-2"
              />
            </div>
          </InlineForm>
        </div>
      </div>
      {isDirty && (
        <FormControls
          isLoading={isLoadingUpdateSettings}
          onCancel={() => reset(defaultFormValues)}
        />
      )}
      <CTAModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={t('Remove Email Trigger')}
        description={t(
          'Removing a trigger will stop all associated automated reminder emails moving forward.'
        )}
        confirmation={t('Are you sure you want to remove it?')}
        action={async () => {
          await handleSubmit(onSubmit)();
          setIsModalOpen(false);
        }}
        isLoading={isLoadingUpdateSettings}
        primaryButtonText={t('Remove')}
      />
    </form>
  );
};

export default Reminders;
